/**
 * NarrativeChat
 * -------------
 *
 * September 2026
 *
 * Follow-up Q&A about a DOE result after the initial AI narrative. The
 * backend (/api/doe/chat) is stateless, so this component owns the
 * conversation and sends the whole thing each turn; the server re-attaches
 * the data context and the narrative. Cleared whenever a new narrative is
 * generated, since old answers would refer to a different result.
 */
import * as React from "react";
import axios from "axios";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { SYSTEM_PROMPT_OVERRIDE_KEY } from "./SystemPromptEditor.js";

function readSystemPromptOverride() {
  try {
    return window.localStorage.getItem(SYSTEM_PROMPT_OVERRIDE_KEY) || undefined;
  } catch {
    return undefined;
  }
}

function NarrativeChat({ doeResult, narrative, userObjective }) {
  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState(null);
  const endRef = React.useRef(null);

  React.useEffect(() => {
    setMessages([]);
    setInput("");
    setError(null);
  }, [narrative]);

  React.useEffect(() => {
    if (endRef.current?.scrollIntoView) endRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, sending]);

  const send = () => {
    const question = input.trim();
    if (!question || sending) return;
    const next = [...messages, { role: "user", content: question }];
    setMessages(next);
    setInput("");
    setError(null);
    setSending(true);
    axios
      .post("/api/doe/chat", {
        doe_result: doeResult,
        narrative,
        messages: next,
        user_objective: userObjective || undefined,
        system_prompt_override: readSystemPromptOverride(),
      })
      .then((res) => setMessages([...next, { role: "assistant", content: res.data.reply || "(empty reply)" }]))
      .catch((err) => {
        setError(err.response?.data?.error || "Failed to get a reply.");
        setMessages(messages);
        setInput(question);
      })
      .finally(() => setSending(false));
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="doe-chat">
      <h4 className="doe-card-title">Ask a follow-up</h4>
      {messages.length > 0 && (
        <div className="doe-chat-log">
          {messages.map((m, i) => (
            <div key={i} className={`doe-chat-msg doe-chat-${m.role}`}>
              {m.role === "assistant" ? (
                <div className="doe-narrative">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                </div>
              ) : (
                m.content
              )}
            </div>
          ))}
          {sending && <div className="doe-chat-msg doe-chat-assistant doe-muted">Thinking...</div>}
          <div ref={endRef} />
        </div>
      )}
      {error && <Message severity="error" text={error} />}
      <div className="doe-row" style={{ alignItems: "flex-end" }}>
        <InputTextarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          rows={2}
          autoResize
          placeholder="e.g. Why does the server count matter more than the arrival rate? (Enter to send, Shift+Enter for a new line)"
          style={{ flex: 1, minWidth: "260px" }}
          disabled={sending}
        />
        <Button label="Send" icon="pi pi-send" onClick={send} disabled={!input.trim() || sending} loading={sending} />
      </div>
    </div>
  );
}

export default NarrativeChat;
